/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Dec 13 2024
 **/

import { EventID, GROUP_POPOVER_ID, POPOVER_ID } from "@/frameworks/core/commons";
import {
	useExtendCallAPIActions,
	useFriendHandlers,
	useFriendListActions,
	useFriendManagerHandler,
	usePeopleListActions,
	usePeopleManagerHandler,
	usePopover
} from "@/frameworks/core/hooks";
import { EventHandler } from "@rfice/eventhandler";

export default function useMiniProfileContentAction() {
	const {
		handlers: { handleChat, handleFollowHome, handleFollowUser }
	} = usePeopleListActions();
	const {
		handlers: { handleSummonFriend, handleFavorites }
	} = useFriendHandlers();
	const { updatePeople } = usePeopleManagerHandler();
	const { updateBlock } = useFriendManagerHandler();
	const { getFriendList, friendList } = useFriendListActions();
	const extendPhoneCallActions = useExtendCallAPIActions();
	const popover = usePopover();
	const handleBlocked = async (userData: Partial<WebServices.User.UserInfo>) => {
		await updatePeople({
			type: "block",
			payload: {
				active: !userData.blockedByMe,
				userID: userData.userId || ""
			}
		});
		await getFriendList();

		updateBlock({
			type: "release",
			payload: {
				blockUser: {
					userId: userData.userId || "",
					username: userData.username || "",
					name: userData.name || "",
					shortDescription: userData.shortDescription,
					profileImagePath: userData.profileImagePath,
					colorProfile: userData.colorProfile || "",
					userStatus: userData.userStatus,
					userStatusEmoji: userData.userStatusEmoji,
					userStatusEmoticonPath: userData.userStatusEmoticonPath,
					activeStatus: userData.activeStatus || "OFFLINE",
					phoneNumber: userData.phoneNumber,
					createdTime: friendList?.get(userData.userId || "")?.createdTime || 0
				}
			}
		});
	};

	const handleHide = (popoverID: POPOVER_ID | GROUP_POPOVER_ID, userID: string) => {
		popover.hide(popoverID);
		EventHandler.emit(EventID.CLOSE_MINI_PROFILE, { userId: userID });
	};

	const handleUserCallToMe = (userID: string) => {
		handleSummonFriend(userID);
	};

	return {
		handleChat,
		handleFollowHome,
		handleFollowUser,
		handleUserCallToMe,
		handleFavorites,
		handleBlocked,
		handleHide,
		handleCall: extendPhoneCallActions.ui.common.handleCall
	};
}
